import { useState } from "react";
import { Search, Send, X, User, Check, Smartphone, Radio } from "lucide-react";
import type { Conversation, Message } from "../../lib/api";
import { getContactDisplayName } from "../views/UnifiedInboxView";

interface ForwardMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message | null;
  conversations: Conversation[];
  onForward: (targetConversationId: string) => Promise<void>;
}

export function ForwardMessageModal({
  isOpen,
  onClose,
  message,
  conversations,
  onForward,
}: ForwardMessageModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !message) return null;

  const filteredConversations = conversations.filter((c) => {
    const term = searchTerm.toLowerCase();
    const name = (
      c.contact?.first_name ||
      c.contact?.last_name ||
      ""
    ).toLowerCase();
    const phone = c.contact?.phone_e164?.toLowerCase() || "";
    return name.includes(term) || phone.includes(term);
  });

  const handleForward = async () => {
    if (!selectedId) return;
    try {
      setIsSubmitting(true);
      await onForward(selectedId);
      onClose();
    } catch (err) {
      console.error("Forwarding failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewText =
    message.content?.text ||
    (message.message_type !== "text" ? `[${message.message_type.toUpperCase()}] Attachment` : "Message");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/80">
          <div>
            <h3 className="text-base font-bold text-slate-800">Forward message to</h3>
            <p className="text-xs text-slate-500 line-clamp-1 mt-0.5 italic">"{previewText}"</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-slate-100 bg-white">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search chats or contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-100 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:bg-white border border-transparent focus:border-emerald-500 transition-all"
              autoFocus
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-xs">
              No matching conversations found
            </div>
          ) : (
            filteredConversations.map((c) => {
              const isSelected = selectedId === c.id;
              const isQr = c.channel_type === "qr_whatsapp";
              const displayName = getContactDisplayName(c.contact);

              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  type="button"
                  className={`w-full text-left p-3 rounded-xl flex items-center justify-between transition-colors ${
                    isSelected
                      ? "bg-emerald-50 border border-emerald-300/80 shadow-xs"
                      : "hover:bg-slate-50 border border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isQr ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      <User className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-slate-800 truncate">
                          {displayName}
                        </span>
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5 ${
                            isQr
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {isQr ? <Smartphone className="w-2.5 h-2.5" /> : <Radio className="w-2.5 h-2.5" />}
                          {isQr ? "QR" : "Meta"}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {c.contact?.phone_e164 || "No phone"}
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 ml-2">
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                        isSelected
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "border-slate-300"
                      }`}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {selectedId ? "1 chat selected" : "Select a chat to forward"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800 rounded-xl hover:bg-slate-200/60 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleForward}
              disabled={!selectedId || isSubmitting}
              className={`px-4 py-2 text-xs font-semibold text-white rounded-xl shadow-xs flex items-center gap-1.5 transition-all ${
                selectedId && !isSubmitting
                  ? "bg-emerald-600 hover:bg-emerald-700 active:scale-98"
                  : "bg-slate-300 cursor-not-allowed opacity-70"
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isSubmitting ? "Forwarding..." : "Forward"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
